import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import mockedPermissions from "logic/permissions/mocked";
import { DataTable, PaginatedList, Spinner, NoSearchResult } from "components/common";
import usePluggableSharedEntityTableElements from "hooks/usePluggableSharedEntityTableElements";
import SharedEntitiesFilter from "./SharedEntitiesFilter";
import SharedEntitiesOverviewItem from "./SharedEntitiesOverviewItem";
const TABLE_HEADER_KEYS = ["entity_name", "entity_type", "owner", "capability"];
const DEFAULT_PAGINATION = {
  page: 1,
  perPage: 10,
  query: ""
};
const StyledPaginatedList = styled(PaginatedList)`
  .pagination {
    margin: 0;
  }
`;
const _sharedEntityOverviewItem = (sharedEntity, { granteeCapabilities } = { granteeCapabilities: void 0 }) => {
  const capability = granteeCapabilities == null ? void 0 : granteeCapabilities[sharedEntity.id];
  const capabilityTitle = mockedPermissions.availableCapabilities[capability];
  return /* @__PURE__ */ React.createElement(SharedEntitiesOverviewItem, { sharedEntity, capabilityTitle });
};
const _loadSharedEntities = (pagination, searchPaginated, setPaginatedEntityShares, setLoading) => {
  setLoading(true);
  searchPaginated(pagination).then((paginatedEntityShares) => {
    setLoading(false);
    setPaginatedEntityShares(paginatedEntityShares);
  });
};
const SharedEntitiesOverview = ({ entityType, searchPaginated, setLoading }) => {
  const { t } = useTranslation("users");
  const [paginatedEntityShares, setPaginatedEntityShares] = useState();
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const { list, context, pagination: { total } = { total: 0 } } = paginatedEntityShares || {};
  const { page, query, additionalQueries } = pagination;
  const { pluggableAttributes } = usePluggableSharedEntityTableElements();
  useEffect(
    () => _loadSharedEntities(pagination, searchPaginated, setPaginatedEntityShares, setLoading),
    [pagination, searchPaginated, setLoading]
  );
  const tableHeaders = useMemo(
    () => [
      ...TABLE_HEADER_KEYS.map((key) => t(`permissions.shared_entities.headers.${key}`)),
      ...pluggableAttributes && pluggableAttributes.attributeNames
    ],
    [pluggableAttributes, t]
  );
  const _handleSearch = (newQuery) => setPagination({ ...pagination, query: newQuery });
  const _handleFilter = (param, value) => setPagination({ ...pagination, query, additionalQueries: { ...additionalQueries, [param]: value } });
  if (!paginatedEntityShares) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", { className: "description" }, t("permissions.shared_entities.found", { total, entityType })), /* @__PURE__ */ React.createElement(
    StyledPaginatedList,
    {
      activePage: page,
      totalItems: total,
      onChange: (newPage, newPerPage) => setPagination({ ...pagination, page: newPage, perPage: newPerPage }),
      useQueryParameter: false
    },
    /* @__PURE__ */ React.createElement(
      DataTable,
      {
        customFilter: /* @__PURE__ */ React.createElement(SharedEntitiesFilter, { onSearch: _handleSearch, onFilter: _handleFilter }),
        dataRowFormatter: (sharedEntity) => _sharedEntityOverviewItem(sharedEntity, context),
        filterKeys: [],
        noDataText: /* @__PURE__ */ React.createElement(NoSearchResult, null, t("permissions.shared_entities.no_results")),
        headers: tableHeaders,
        id: "shared-entities",
        rowClassName: "no-bm",
        rows: list.toJS(),
        sortByKey: "type"
      }
    )
  ));
};
export default SharedEntitiesOverview;
